import type { CompletedTask } from '../services/api';

interface TaskCompletionPromptProps {
  completedTask: CompletedTask;
  remainingTaskCount: number;
  onStartAnotherTask: () => void;
  onLeaveStore: () => void;
  isLoading?: boolean;
  /** Optional label override for the "Leave Store" action. Consolidated manual
   *  entry (#1863) passes "Confirm Departure (HH:MM)" so the rep sees which
   *  pre-entered timestamp will be recorded. */
  leaveButtonLabel?: string;
  /** When set, Leave Store is disabled and this names the still-open task(s)
   *  the rep must finish first. */
  leaveBlockedReason?: string;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export default function TaskCompletionPrompt({
  completedTask,
  remainingTaskCount,
  onStartAnotherTask,
  onLeaveStore,
  isLoading = false,
  leaveButtonLabel,
  leaveBlockedReason,
}: TaskCompletionPromptProps) {
  const isSet = completedTask.task_type === 'set';

  return (
    <div className="rounded-xl bg-white p-6 shadow-card">
      {/* Success Header */}
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800">Task Completed!</h2>
        <p className="mt-1 text-sm text-slate-500">Great work on this task</p>
      </div>

      {/* Completed Task Summary */}
      <div className={`mb-6 rounded-lg p-4 ${isSet ? 'bg-purple-50 border border-purple-200' : 'bg-blue-50 border border-blue-200'}`}>
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${isSet ? 'bg-purple-100' : 'bg-blue-100'}`}>
            {isSet ? (
              <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-800">
              {completedTask.project_description}
            </p>
            {completedTask.client_name && (
              <p className="text-xs text-slate-500">{completedTask.client_name}</p>
            )}
            <div className="mt-2 flex items-center gap-4 text-sm">
              <span className={isSet ? 'text-purple-700' : 'text-blue-700'}>
                <span className="font-medium">{formatDuration(completedTask.duration_minutes)}</span>
              </span>
              <span className="text-slate-500">
                {new Date(completedTask.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                {' - '}
                {new Date(completedTask.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Prompt */}
      {remainingTaskCount > 0 ? (
        <div className="mb-4 rounded-lg bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
              <svg className="h-4 w-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-amber-800">
                {remainingTaskCount} more task{remainingTaskCount > 1 ? 's' : ''} available
              </p>
              <p className="mt-1 text-sm text-amber-700">
                Would you like to complete another task while you're here?
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-4 rounded-lg bg-slate-50 p-4">
          <p className="text-center text-sm text-slate-600">
            No more tasks available at this store.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {remainingTaskCount > 0 && (
          <button
            onClick={onStartAnotherTask}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 py-4 font-semibold text-white shadow-lg transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Start Another Task
          </button>
        )}
        <button
          onClick={onLeaveStore}
          disabled={isLoading || !!leaveBlockedReason}
          className={`flex w-full items-center justify-center gap-2 rounded-xl py-4 font-semibold transition-all active:scale-[0.98] disabled:opacity-50 ${
            remainingTaskCount > 0
              ? 'border-2 border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              : 'bg-primary-600 text-white shadow-lg hover:bg-primary-700'
          }`}
        >
          {isLoading ? (
            <>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
              Processing...
            </>
          ) : (
            <>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {leaveButtonLabel ?? 'Leave Store'}
            </>
          )}
        </button>
        {leaveBlockedReason && (
          <p className="text-center text-sm text-amber-700">
            Finish your open task first: <span className="font-medium">{leaveBlockedReason}</span>
          </p>
        )}
      </div>
    </div>
  );
}
